import React, { useState } from "react";
import {
  SpaceBetween,
  Button,
  Container,
  Header,
  Form,
  FormField,
  Input,
  Alert,
  ColumnLayout,
  Box,
} from "@cloudscape-design/components";
import { useApi } from "../Api";
import { Playtest } from "./Playtest";

// Focus Group login spike.
//
// Traces the whole chain: Stake pre-prod credentials -> Pragma login (via the
// portal backend) -> Pragma session -> Playtest stream launched carrying that
// identity.
//
// This is a spike, not the Focus Group landing page. It deliberately sits inside
// the normal Cognito-gated portal so it is staff-only and needs no new
// infrastructure. The public, un-chromed page on focusgroup.wde.ninja is
// separate work -- see web-infra/focus-group-page/PLAN-FABLE.md.
//
// The browser never sees the Pragma tokens. Login returns an opaque grant id;
// the server redeems it and injects the session as stream launch args.
export default function FocusGroupSpike() {
  const Api = useApi();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [tfaCode, setTfaCode] = useState("");
  const [loginToken, setLoginToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [streaming, setStreaming] = useState(false);

  const needsTfa = !!loginToken;
  const canSubmit =
    !submitting &&
    (needsTfa
      ? tfaCode.trim().length > 0
      : email.trim().length > 0 && password.length > 0);

  const submit = async () => {
    setSubmitting(true);
    setResult(null);
    try {
      // Backend answers 200 with { success, message, ... } for both outcomes,
      // because the shared Api helper discards the body on non-2xx.
      const res = await Api.post("FocusGroupSpike/Login/", {
        email: email.trim(),
        password,
        tfaCode: tfaCode.trim(),
        loginToken,
      });
      if (res.tfaRequired) {
        setLoginToken(res.loginToken || "");
        setResult({ success: false, message: "Two factor code required" });
      } else {
        setResult(res);
        if (res.success) setLoginToken("");
      }
    } catch (err) {
      setResult({ success: false, message: "Request failed. Please try again." });
    }
    setSubmitting(false);
  };

  const startStream = () => {
    // Drop any cached Playtest session. Without this the player reconnects to an
    // existing session, which was started without our launch args and therefore
    // would not carry this player's identity.
    try {
      localStorage.removeItem("session_Playtest");
    } catch {
      /* private mode, ignore */
    }
    setStreaming(true);
  };

  const reset = () => {
    setResult(null);
    setStreaming(false);
    setPassword("");
    setTfaCode("");
    setLoginToken("");
  };

  if (streaming && result && result.success) {
    return (
      <SpaceBetween size="l">
        <Alert type="info" header={`Streaming as ${result.displayName}#${result.discriminator}`}>
          The stream is launching with this player's Pragma session passed as
          launch arguments, and no name override. The intended behaviour is that
          the client links the player session from those tokens, takes its name
          from Pragma, and goes into matchmaking. Nothing in the game client
          reads the tokens yet, so for now it will still show its own login.
        </Alert>
        <Playtest Environment="Playtest" FocusGroupGrantId={result.grantId} autoStart />
        <Button onClick={reset}>Start over</Button>
      </SpaceBetween>
    );
  }

  return (
    <Container
      header={
        <Header
          variant="h2"
          description="Spike: Stake pre-prod login through Pragma, then into a Playtest stream carrying that identity."
        >
          Focus Group Login Spike
        </Header>
      }
    >
      <SpaceBetween size="l">
        <Alert type="info">
          Staff-only spike. Sign in with a Stake pre-production
          account (steak-kitchen-casino.com). Stake allows one active session per
          user, so this can sign that account out elsewhere.
        </Alert>

        <Form
          actions={
            <Button
              variant="primary"
              loading={submitting}
              disabled={!canSubmit}
              onClick={submit}
            >
              {needsTfa ? "Submit code" : "Log in with Stake"}
            </Button>
          }
        >
          <SpaceBetween size="m">
            <FormField label="Stake email">
              <Input
                type="email"
                value={email}
                onChange={({ detail }) => setEmail(detail.value)}
                placeholder="you@example.com"
                disabled={submitting || needsTfa}
              />
            </FormField>
            <FormField label="Stake password">
              <Input
                type="password"
                value={password}
                onChange={({ detail }) => setPassword(detail.value)}
                disabled={submitting || needsTfa}
              />
            </FormField>
            {needsTfa && (
              <FormField
                label="Two factor code"
                description="This account has 2FA enabled."
              >
                <Input
                  value={tfaCode}
                  onChange={({ detail }) => setTfaCode(detail.value)}
                  disabled={submitting}
                />
              </FormField>
            )}
          </SpaceBetween>
        </Form>

        {result && result.success && (
          <Alert type="success" header="Pragma session established">
            <SpaceBetween size="m">
              <ColumnLayout columns={2} variant="text-grid">
                <div>
                  <Box variant="awsui-key-label">Display name</Box>
                  <div>
                    {result.displayName}
                    {result.discriminator ? `#${result.discriminator}` : ""}
                  </div>
                </div>
                <div>
                  <Box variant="awsui-key-label">Identity provider</Box>
                  <div>{result.idProvider}</div>
                </div>
                <div>
                  <Box variant="awsui-key-label">Pragma player id</Box>
                  <div>{result.playerId}</div>
                </div>
                <div>
                  <Box variant="awsui-key-label">Pragma social id</Box>
                  <div>{result.socialId}</div>
                </div>
                <div>
                  <Box variant="awsui-key-label">Game shard</Box>
                  <div>{result.gameShardId}</div>
                </div>
              </ColumnLayout>
              <Button variant="primary" onClick={startStream}>
                Enter Playtest stream
              </Button>
            </SpaceBetween>
          </Alert>
        )}

        {result && !result.success && (
          <Alert type="error" header="Could not log in">
            {result.message || "Unknown error"}
          </Alert>
        )}
      </SpaceBetween>
    </Container>
  );
}
